import { useState, useEffect } from 'react';
import axios from '../api/axios';
import './WhatsAppSettings.css';

const WhatsAppSettings = () => {
  const [instance, setInstance] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [connectionState, setConnectionState] = useState(null);
  const [settings, setSettings] = useState([]);
  const [messageLogs, setMessageLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('connection');
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');

  // New recipient selection states
  const [recipientType, setRecipientType] = useState('custom'); // 'custom', 'customers', 'employees', 'vendors'
  const [recipients, setRecipients] = useState([]);
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [recipientsSearch, setRecipientsSearch] = useState('');
  const [sendingProgress, setSendingProgress] = useState(null);

  useEffect(() => {
    loadInstance();
    loadSettings();
    loadMessageLogs();
  }, []);

  useEffect(() => {
    if (recipientType !== 'custom') {
      fetchRecipientList(recipientType);
    } else {
      setRecipients([]);
      setSelectedPhones([]);
    }
  }, [recipientType]);

  const fetchRecipientList = async (type) => {
    setLoading(true);
    try {
      let endpoint = '';
      if (type === 'customers') endpoint = '/customers?size=1000';
      else if (type === 'employees') endpoint = '/employees?size=1000';
      else if (type === 'vendors') endpoint = '/vendors?size=1000';

      const response = await axios.get(endpoint);
      let data = [];
      if (type === 'customers') {
        data = response.data.data?.content || response.data.content || response.data.data || [];
      } else if (type === 'employees') {
        data = response.data.employees || response.data.data?.employees || response.data.content || response.data.data || [];
      } else if (type === 'vendors') {
        data = response.data.data?.content || response.data.content || response.data.data || [];
      }

      const formatted = (Array.isArray(data) ? data : [])
        .filter(item => item && item.phone)
        .map(item => ({
          id: item.id,
          name: item.name || 'Unnamed',
          phone: item.phone
        }));

      setRecipients(formatted);
      setSelectedPhones([]);
    } catch (error) {
      console.error('Error fetching recipient list:', error);
      alert('Error fetching recipient list: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadInstance = async () => {
    try {
      const response = await axios.get('/whatsapp/instance');
      if (response.data && response.data.id) {
        setInstance(response.data);
      }
    } catch (error) {
      console.error('Error loading instance:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get('/whatsapp/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadMessageLogs = async () => {
    try {
      const response = await axios.get('/whatsapp/message-logs');
      setMessageLogs(response.data);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    setQrCode(null);
    try {
      const response = await axios.post('/whatsapp/connect');
      console.log('Connect response:', response.data);
      
      if (response.data && response.data.error) {
        alert('Error: ' + response.data.error);
        return;
      }
      
      if (response.data && response.data.qrcode) {
        setQrCode(response.data.qrcode);
        alert('QR Code loaded! Scan it with WhatsApp to connect.');
      } else if (response.data && response.data.base64) {
        setQrCode(response.data.base64);
        alert('QR Code loaded! Scan it with WhatsApp to connect.');
      } else {
        console.log('No QR code in response, trying refresh...');
        // Try to refresh QR code
        setTimeout(() => handleRefreshQRCode(), 1000);
      }
      loadInstance();
    } catch (error) {
      console.error('Error connecting:', error);
      alert('Error connecting: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshQRCode = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/whatsapp/qr-code');
      console.log('QR code response:', response.data);
      
      if (response.data && response.data.error) {
        alert('Error: ' + response.data.error);
        return;
      }
      
      if (response.data && response.data.qrcode) {
        setQrCode(response.data.qrcode);
        alert('QR Code refreshed! Scan it with WhatsApp to connect.');
      } else if (response.data && response.data.base64) {
        setQrCode(response.data.base64);
        alert('QR Code refreshed! Scan it with WhatsApp to connect.');
      } else {
        console.log('No QR code found in response:', response.data);
        alert('QR code not available. Please click "Connect WhatsApp" first.');
      }
    } catch (error) {
      console.error('Error loading QR code:', error);
      alert('Error loading QR code: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckConnection = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/whatsapp/connection-state');
      setConnectionState(response.data);
      if (response.data.state === 'open') {
        alert('WhatsApp is connected!');
        setQrCode(null);
        loadInstance();
      } else {
        alert('Connection state: ' + response.data.state);
      }
    } catch (error) {
      alert('Error checking connection: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSetting = async (settingId, updates) => {
    try {
      await axios.put(`/whatsapp/settings/${settingId}`, updates);
      loadSettings();
      alert('Setting updated successfully');
    } catch (error) {
      alert('Error updating setting: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSendTestMessage = async () => {
    if (recipientType === 'custom') {
      if (!testPhone || !testMessage) {
        alert('Please enter phone number and message');
        return;
      }
      
      setLoading(true);
      setSendingProgress('Sending message...');
      try {
        await axios.post('/whatsapp/send-message', {
          phone: testPhone,
          message: testMessage
        });
        alert('Test message sent successfully');
        setTestPhone('');
        setTestMessage('');
        loadMessageLogs();
      } catch (error) {
        alert('Error sending message: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
        setSendingProgress(null);
      }
    } else {
      if (selectedPhones.length === 0 || !testMessage) {
        alert('Please select at least one recipient and enter a message');
        return;
      }

      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < selectedPhones.length; i++) {
        const phone = selectedPhones[i];
        setSendingProgress(`Sending message ${i + 1} of ${selectedPhones.length}...`);
        try {
          await axios.post('/whatsapp/send-message', {
            phone: phone,
            message: testMessage
          });
          successCount++;
        } catch (error) {
          console.error(`Error sending to ${phone}:`, error);
          failCount++;
        }
      }

      alert(`Completed sending messages. Success: ${successCount}, Failed: ${failCount}`);
      setSelectedPhones([]);
      setTestMessage('');
      setSendingProgress(null);
      setLoading(false);
      loadMessageLogs();
    }
  };

  const renderConnectionTab = () => (
    <div className="wa-tab-content">
      <div className="wa-connection-section">
        <h3>WhatsApp Connection</h3>
        
        {instance && (
          <div className="wa-connection-info">
            <div className="wa-info-item">
              <span className="wa-label">Instance Name:</span>
              <span>{instance.instanceName}</span>
            </div>
            <div className="wa-info-item">
              <span className="wa-label">Status:</span>
              <span className={`wa-status ${instance.isConnected ? 'connected' : 'disconnected'}`}>
                {instance.isConnected ? '✓ Connected' : '✗ Not Connected'}
              </span>
            </div>
            {instance.phoneNumber && (
              <div className="wa-info-item">
                <span className="wa-label">Phone:</span>
                <span>{instance.phoneNumber}</span>
              </div>
            )}
            {instance.lastConnectedAt && (
              <div className="wa-info-item">
                <span className="wa-label">Last Connected:</span>
                <span>{new Date(instance.lastConnectedAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        <div className="wa-button-group">
          {!instance?.isConnected && (
            <>
              <button onClick={handleConnect} disabled={loading} className="wa-btn-primary">
                {loading ? 'Connecting...' : 'Connect WhatsApp'}
              </button>
              {qrCode && (
                <button onClick={handleRefreshQRCode} disabled={loading} className="wa-btn-secondary">
                  Refresh QR Code
                </button>
              )}
            </>
          )}
          <button onClick={handleCheckConnection} disabled={loading} className="wa-btn-secondary">
            Check Status
          </button>
        </div>

        {qrCode && (
          <div className="wa-qr-code-section">
            <h4>Scan QR Code with WhatsApp</h4>
            <div className="wa-qr-code">
              <img src={qrCode} alt="QR Code" />
            </div>
            <p className="wa-qr-instructions">
              Open WhatsApp on your phone → Settings → Linked Devices → Link a Device → Scan this QR code
            </p>
          </div>
        )}

        {connectionState && (
          <div className="wa-connection-state">
            <h4>Connection State:</h4>
            <pre>{JSON.stringify(connectionState, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="wa-tab-content">
      <h3>Notification Settings</h3>
      <p className="wa-help-text">Configure automated WhatsApp notifications for different events</p>
      
      <div className="wa-settings-grid">
        {settings.map(setting => (
          <div key={setting.id} className="wa-setting-card">
            <div className="wa-setting-header">
              <h4>{setting.notificationType.replace(/_/g, ' ')}</h4>
              <label className="wa-toggle">
                <input
                  type="checkbox"
                  checked={setting.isEnabled}
                  onChange={(e) => handleUpdateSetting(setting.id, {...setting, isEnabled: e.target.checked})}
                />
                <span className="wa-toggle-slider"></span>
              </label>
            </div>
            
            {setting.isEnabled && (
              <div className="wa-setting-details">
                <div className="wa-recipients">
                  <label>
                    <input
                      type="checkbox"
                      checked={setting.sendToCustomer}
                      onChange={(e) => handleUpdateSetting(setting.id, {...setting, sendToCustomer: e.target.checked})}
                    />
                    Send to Customer
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={setting.sendToAssignedPerson}
                      onChange={(e) => handleUpdateSetting(setting.id, {...setting, sendToAssignedPerson: e.target.checked})}
                    />
                    Send to Assigned Person
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={setting.sendToAdmin}
                      onChange={(e) => handleUpdateSetting(setting.id, {...setting, sendToAdmin: e.target.checked})}
                    />
                    Send to Admin (all company admins)
                  </label>
                </div>

                {setting.sendToCustomer && (
                  <div className="wa-form-group">
                    <label>Customer Message Template:</label>
                    <textarea
                      value={setting.templateMessage}
                      onChange={(e) => {
                        const updated = {...setting, templateMessage: e.target.value};
                        setSettings(settings.map(s => s.id === setting.id ? updated : s));
                      }}
                      onBlur={() => handleUpdateSetting(setting.id, setting)}
                      rows={3}
                    />
                    <small>Available variables: {'{customer_name}, {project_name}, {amount}, {transaction_id}, etc.'}</small>
                  </div>
                )}

                {(setting.sendToAssignedPerson || setting.sendToAdmin) && (
                  <div className="wa-form-group">
                    <label>Employee Message Template:</label>
                    <textarea
                      value={setting.employeeTemplateMessage || ''}
                      onChange={(e) => {
                        const updated = {...setting, employeeTemplateMessage: e.target.value};
                        setSettings(settings.map(s => s.id === setting.id ? updated : s));
                      }}
                      onBlur={() => handleUpdateSetting(setting.id, setting)}
                      rows={3}
                    />
                    <small>Available variables: {'{assigned_person}, {customer_name}, {project_name}, {enquiry_id}, etc.'}</small>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTestTab = () => {
    const filteredRecipients = recipients.filter(r => 
      r.name.toLowerCase().includes(recipientsSearch.toLowerCase()) || 
      r.phone.includes(recipientsSearch)
    );

    const handleToggleRecipient = (phone) => {
      if (selectedPhones.includes(phone)) {
        setSelectedPhones(selectedPhones.filter(p => p !== phone));
      } else {
        setSelectedPhones([...selectedPhones, phone]);
      }
    };

    const handleSelectAllFiltered = () => {
      const filteredPhones = filteredRecipients.map(r => r.phone);
      const toAdd = filteredPhones.filter(p => !selectedPhones.includes(p));
      setSelectedPhones([...selectedPhones, ...toAdd]);
    };

    const handleDeselectAllFiltered = () => {
      const filteredPhones = filteredRecipients.map(r => r.phone);
      setSelectedPhones(selectedPhones.filter(p => !filteredPhones.includes(p)));
    };

    return (
      <div className="wa-tab-content">
        <h3>Custom Messages</h3>
        
        {sendingProgress && (
          <div className="wa-progress-banner">
            <span>ℹ️</span>
            <span>{sendingProgress}</span>
          </div>
        )}

        <div className="wa-test-section">
          <div className="wa-form-group">
            <label>Recipient Type:</label>
            <select
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="custom">Custom Number (Enter manually)</option>
              <option value="customers">Customers</option>
              <option value="employees">Employees</option>
              <option value="vendors">Vendors</option>
            </select>
          </div>

          {recipientType === 'custom' ? (
            <div className="wa-form-group">
              <label>Recipient Phone Number:</label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+1234567890"
              />
              <small>Include country code (e.g., +1 for US, +91 for India)</small>
            </div>
          ) : (
            <div className="wa-form-group">
              <label>Select Recipients:</label>
              <div className="wa-recipient-select-container">
                <input
                  type="text"
                  className="wa-recipient-filter"
                  placeholder={`Search ${recipientType}...`}
                  value={recipientsSearch}
                  onChange={(e) => setRecipientsSearch(e.target.value)}
                />
                
                <div className="wa-recipient-actions">
                  <span>Selected: <strong>{selectedPhones.length}</strong> of {filteredRecipients.length} matching</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" onClick={handleSelectAllFiltered} className="wa-btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }}>
                      Select All
                    </button>
                    <button type="button" onClick={handleDeselectAllFiltered} className="wa-btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }}>
                      Clear
                    </button>
                  </div>
                </div>

                <div className="wa-recipient-list">
                  {filteredRecipients.map(r => (
                    <div 
                      key={r.id + '-' + r.phone} 
                      className="wa-recipient-item"
                      onClick={() => handleToggleRecipient(r.phone)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPhones.includes(r.phone)}
                        onChange={() => {}} // Handled by div onClick
                      />
                      <span>{r.name} ({r.phone})</span>
                    </div>
                  ))}
                  {filteredRecipients.length === 0 && (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                      No matching recipients found
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="wa-form-group">
            <label>Custom Message:</label>
            <textarea
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              rows={4}
              placeholder="Enter your custom message here..."
            />
          </div>

          <button onClick={handleSendTestMessage} disabled={loading} className="wa-btn-primary">
            Send Test Message
          </button>
        </div>

        <div className="wa-logs-section">
          <h3>Message Logs</h3>
          <div className="wa-logs-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Phone</th>
                  <th>Message</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {messageLogs.map(log => (
                  <tr key={log.id}>
                    <td>{new Date(log.createdAt).toLocaleString()}</td>
                    <td>{log.notificationType || 'N/A'}</td>
                    <td>{log.recipientPhone}</td>
                    <td className="wa-message-cell">{log.messageText}</td>
                    <td>
                      <span className={`wa-status-badge ${log.status.toLowerCase()}`}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {messageLogs.length === 0 && (
                  <tr>
                    <td colSpan="5" className="wa-no-data">No messages sent yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="wa-container">
      <div className="wa-header">
        <h1>WhatsApp Integration</h1>
        <p>Connect WhatsApp and configure automated notifications</p>
      </div>

      <div className="wa-tabs">
        <button 
          className={`wa-tab ${activeTab === 'connection' ? 'active' : ''}`}
          onClick={() => setActiveTab('connection')}
        >
          Connection
        </button>
        <button 
          className={`wa-tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
        <button 
          className={`wa-tab ${activeTab === 'test' ? 'active' : ''}`}
          onClick={() => setActiveTab('test')}
        >
          Custom Messages
        </button>
      </div>

      {activeTab === 'connection' && renderConnectionTab()}
      {activeTab === 'notifications' && renderNotificationsTab()}
      {activeTab === 'test' && renderTestTab()}
    </div>
  );
};

export default WhatsAppSettings;
